/* Noirarchy Coven pop-up (window.Noir.popup). Deferred and global; loads after noir-ui.js
   and noir-coven.js. Markup: sections/noir-popup.liquid (dialog[data-noir-popup]); styles:
   assets/noir-section-popup.css.

   When it opens by itself
     - Never once this browser joined the Coven (Noir.coven.joined() or localStorage
       'noir-coven-joined'), and never by itself in the theme editor (there it opens while
       the section is selected).
     - Once per visitor: localStorage 'noir-popup-shown' (a timestamp) is written as it opens,
       and 'noir-popup-closed' is cleared. It can come back only after it was closed (the
       close writes the 'noir-popup-closed' timestamp) and data-suppress days have passed.
     - If that flag cannot be written (storage blocked), it does not open by itself, so it
       can never show on every page.
     - It opens once, on whichever comes first: data-delay seconds after the page is ready,
       or a scroll depth of data-scroll percent.
     - It waits while the cart drawer, another dialog or modal, a header menu or a focused
       form field is in use, or while the tab is hidden, and opens when that ends.
   After a post from its own form (Noir.coven says the sent form is this one, or the hash
   is #<form id>) it opens at once: the success state, with focus on the title, or the
   form with its error, with focus on the field.

   Closing: the X, "Maybe in another life", Escape and a click on the backdrop, through the
   noir-ui.js dialog (focus inside and returned on close, page scroll held while open).
   On a phone, a tap on the backdrop while the email field is in use only puts the keyboard
   away. "Start shopping" closes it and follows its link. No animation under reduced motion
   (CSS). Opening focuses the heading; Tab goes on to the email field.

   Phone sheet: while open, the sheet's bottom follows the visible bottom of the screen
   (above the keyboard) and its height stays within the visible height; the field in use
   and its button are kept in view inside the sheet (see "Phone sheet" below).

   Openers: a click on any [data-noir-popup-open] opens it the same way as Noir.popup.open(),
   and also cancels the pending timed open on this page.

   API, for testing: Noir.popup.open() opens it now, past every rule, and writes nothing;
   Noir.popup.close() closes it. Storage access is wrapped in try/catch. */
(function () {
  'use strict';

  var Noir = (window.Noir = window.Noir || {});
  if (Noir.popup && Noir.popup.__ready) return;

  var SHOWN = 'noir-popup-shown';
  var CLOSED = 'noir-popup-closed';
  var JOINED = 'noir-coven-joined';
  var DAY = 24 * 60 * 60 * 1000;
  var RETRY = 700;

  /* Elements that, while open or in use, hold the pop-up back. */
  var BUSY = [
    'cart-drawer.active',
    'modal-dialog[open]',
    'product-modal[open]',
    'quick-add-modal[open]',
    'details-modal details[open]',
    'header-drawer details[open]',
    'header-menu details[open]',
    '.section-header details[open]'
  ].join(',');
  var FIELD = 'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="range"]):not([type="color"]):not([type="file"]):not([type="image"]), textarea, select, [contenteditable=""], [contenteditable="true"]';

  /* ---------- Storage ---------- */
  function store() {
    try {
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }
  function read(key) {
    try {
      var s = store();
      return s ? s.getItem(key) : null;
    } catch (e) {
      return null;
    }
  }
  function write(key, value) {
    try {
      var s = store();
      if (!s) return false;
      s.setItem(key, String(value));
      return s.getItem(key) === String(value);
    } catch (e) {
      return false;
    }
  }
  function erase(key) {
    try {
      var s = store();
      if (s) s.removeItem(key);
    } catch (e) {}
  }

  /* ---------- Helpers ---------- */
  function designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function joined() {
    if (Noir.coven && typeof Noir.coven.joined === 'function' && Noir.coven.joined()) return true;
    return read(JOINED) === '1';
  }

  function currentHash() {
    var raw = (window.location.hash || '').replace(/^#/, '');
    try {
      return decodeURIComponent(raw);
    } catch (e) {
      return raw;
    }
  }

  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  function number(value, fallback, min, max) {
    var n = parseInt(value, 10);
    if (isNaN(n)) n = fallback;
    return Math.min(Math.max(n, min), max);
  }

  /* ---------- State ---------- */
  var dialog = null;
  var config = { delay: 8, scroll: 40, suppress: 30 };
  var mode = null; /* 'auto' | 'result' | 'manual' | 'editor' while open, else null */
  var armed = false;
  var fired = false;
  var delayTimer = 0;
  var retryTimer = 0;
  var scrollFrame = 0;
  var wasBusy = false;

  function find() {
    return document.querySelector('dialog[data-noir-popup]');
  }

  function formRoot() {
    return dialog ? dialog.querySelector('[data-noir-coven]') : null;
  }

  function formState() {
    var root = formRoot();
    return root ? root.getAttribute('data-state') || 'ask' : 'ask';
  }

  function isOpen() {
    return !!(dialog && (dialog.open || dialog.hasAttribute('open')));
  }

  /* ---------- Rules ---------- */
  function eligible() {
    if (!dialog || !document.contains(dialog)) return false;
    if (designMode() || joined()) return false;
    if (read(SHOWN) === null) return true;
    var closed = parseInt(read(CLOSED), 10);
    if (!closed) return false;
    return Date.now() - closed >= config.suppress * DAY;
  }

  /* The page loaded after a post from this pop-up's own form. The router has already put
     every form that was not sent back to its ask state. */
  function sentHere() {
    var root = formRoot();
    if (!root) return false;
    var state = root.getAttribute('data-state');
    if (state !== 'success' && state !== 'error') return false;
    var id = root.getAttribute('data-noir-coven') || dialog.getAttribute('data-noir-popup-form');
    var sent = Noir.coven && typeof Noir.coven.sentId === 'function' ? Noir.coven.sentId() : null;
    return sent === id || currentHash() === id;
  }

  function busy() {
    if (document.hidden) return true;
    var top = Noir.dialog && typeof Noir.dialog.top === 'function' ? Noir.dialog.top() : null;
    if (top && top !== dialog) return true;
    var open = document.querySelectorAll('dialog[open]');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== dialog) return true;
    }
    if (document.querySelector(BUSY)) return true;
    var body = document.body;
    if (body && /(^|\s)overflow-hidden(-[a-z]+)?(\s|$)/.test(body.className)) return true;
    var active = document.activeElement;
    if (active && active !== body && !dialog.contains(active) && active.matches && active.matches(FIELD)) return true;
    return false;
  }

  /* ---------- Phone sheet ---------- */
  /* On a phone the pop-up is a sheet on the bottom edge. When the keyboard opens, only the
     visible part of the page (the visual viewport) gets shorter: a fixed sheet stays on the
     page's bottom edge, under the keyboard, and the browser pans the view to the field,
     which moves the sheet while someone types. While the pop-up is open this keeps the
     sheet's bottom on the visible bottom (--noir-popup-lift) and its height within the
     visible height (--noir-popup-room). The sheet scrolls inside itself, and the field and
     its button are scrolled into view there, never by moving the page. If the browser
     scrolled the page anyway, closing puts it back where it was. */
  var PHONE = '(max-width: 749px)';
  var probe = null;
  var fitFrame = 0;
  var sheetOn = false;
  var heldY = null;
  var pressWhileTyping = false;

  function phone() {
    return !!(window.matchMedia && window.matchMedia(PHONE).matches);
  }

  function typing() {
    var active = document.activeElement;
    return !!(dialog && active && active !== document.body && dialog.contains(active) && active.matches && active.matches(FIELD));
  }

  /* The page's bottom edge as a fixed element sees it, in the coordinates of the visual
     viewport's offsetTop. */
  function pageBottom() {
    if (!probe || !document.contains(probe)) {
      /* Important, so theme rules for empty elements cannot hide or move it. */
      probe = document.createElement('div');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText = 'position:fixed!important;display:block!important;top:auto!important;left:0!important;bottom:0!important;width:1px!important;height:0!important;margin:0!important;visibility:hidden!important;pointer-events:none!important';
      document.body.appendChild(probe);
    }
    return probe.getBoundingClientRect().top;
  }

  function unfit() {
    if (!dialog) return;
    dialog.style.removeProperty('--noir-popup-lift');
    dialog.style.removeProperty('--noir-popup-room');
    dialog.style.removeProperty('--noir-popup-trim');
    dialog.classList.remove('is-keyboard');
  }

  /* The field in use, the button and any error under it, in view inside the sheet: from
     the top when they fit that way, else with the form's entry block (field, button,
     error, consent line) at the top of the scroll area, so no line of the heading shows
     cut in half. */
  function reveal() {
    var scroller = dialog && dialog.querySelector('.noir-popup__scroll');
    var field = document.activeElement;
    if (!scroller || !field || !scroller.contains(field)) return;
    dialog.style.removeProperty('--noir-popup-trim');
    var current = scroller.scrollTop;
    var origin = scroller.getBoundingClientRect().top - current;
    var form = field.closest('form');
    var entry = field.closest('.noir-coven__entry') || field;
    var end = field.getBoundingClientRect().bottom;
    var below = form ? form.querySelectorAll('[type="submit"], [data-noir-coven-error]:not([hidden])') : [];
    for (var i = 0; i < below.length; i++) end = Math.max(end, below[i].getBoundingClientRect().bottom);
    var target = end + 12 - origin <= scroller.clientHeight ? 0 : entry.getBoundingClientRect().top - origin;
    /* Too little below the form to scroll it to the top: the sheet gets shorter instead
       (--noir-popup-trim), while the keyboard is up. */
    var room = scroller.scrollHeight - scroller.clientHeight;
    if (target > room + 1 && dialog.classList.contains('is-keyboard')) {
      dialog.style.setProperty('--noir-popup-trim', Math.ceil(target - room) + 'px');
    }
    if (Math.abs(target - scroller.scrollTop) >= 1) scroller.scrollTop = target;
  }

  /* A soft edge at the top of the sheet's scroll area once it is scrolled. */
  function onSheetScroll(e) {
    var scroller = e.currentTarget;
    scroller.classList.toggle('is-scrolled', scroller.scrollTop > 2);
  }

  function fit() {
    fitFrame = 0;
    var vv = window.visualViewport;
    if (!sheetOn || !isOpen() || !vv || !phone() || vv.scale > 1.05) {
      unfit();
      return;
    }
    var bottom = pageBottom();
    var lift = Math.max(0, Math.round(bottom - (vv.offsetTop + vv.height)));
    dialog.style.setProperty('--noir-popup-lift', lift + 'px');
    dialog.style.setProperty('--noir-popup-room', Math.floor(vv.height) + 'px');
    /* The keyboard is up: a smaller seal leaves more of the sheet to the form. */
    dialog.classList.toggle('is-keyboard', bottom - vv.height > 120);
    if (typing()) reveal();
    else dialog.style.removeProperty('--noir-popup-trim');
  }

  function queueFit() {
    if (!fitFrame) fitFrame = window.requestAnimationFrame(fit);
  }

  function scrollToY(y) {
    try {
      window.scrollTo({ top: y, left: window.pageXOffset, behavior: 'instant' });
    } catch (e) {
      window.scrollTo(window.pageXOffset, y);
    }
  }

  function startSheet() {
    if (sheetOn || !dialog) return;
    sheetOn = true;
    heldY = window.pageYOffset;
    var vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', queueFit);
      vv.addEventListener('scroll', queueFit);
    }
    window.addEventListener('resize', queueFit);
    /* The first fit lands at once, not as a slide. */
    dialog.style.transition = 'none';
    fit();
    dialog.getBoundingClientRect();
    dialog.style.transition = '';
  }

  function stopSheet() {
    if (!sheetOn) return;
    sheetOn = false;
    var vv = window.visualViewport;
    if (vv) {
      vv.removeEventListener('resize', queueFit);
      vv.removeEventListener('scroll', queueFit);
    }
    window.removeEventListener('resize', queueFit);
    if (fitFrame) window.cancelAnimationFrame(fitFrame);
    fitFrame = 0;
    unfit();
    if (heldY !== null && Math.abs(window.pageYOffset - heldY) >= 1) scrollToY(heldY);
    heldY = null;
  }

  /* ---------- Open and close ---------- */
  /* The seal is lazy inside the closed dialog; fetch the one this screen shows once the
     pop-up may open (the other loads if the window is resized past the breakpoint). */
  function warmSeal() {
    var img = dialog && dialog.querySelector(phone() ? '.noir-popup__seal--sheet' : '.noir-popup__seal--card');
    if (img && img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
  }

  /* Name the dialog by the heading of the state on show. */
  function syncLabel() {
    if (!dialog) return;
    if (!dialog.hasAttribute('data-noir-label')) {
      dialog.setAttribute('data-noir-label', dialog.getAttribute('aria-labelledby') || '');
      dialog.setAttribute('data-noir-described', dialog.getAttribute('aria-describedby') || '');
    }
    var title = formState() === 'success' ? dialog.querySelector('[data-noir-coven-success] [id][data-noir-coven-focus]') : null;
    if (title) {
      dialog.setAttribute('aria-labelledby', title.id);
      dialog.removeAttribute('aria-describedby');
      return;
    }
    var label = dialog.getAttribute('data-noir-label');
    var described = dialog.getAttribute('data-noir-described');
    if (label) dialog.setAttribute('aria-labelledby', label);
    if (described) dialog.setAttribute('aria-describedby', described);
  }

  /* The ask state focuses the heading (not the X, which would show its ring as the pop-up
     opens); Tab goes on to the email field. */
  function focusState() {
    var state = formState();
    if (state === 'success') {
      focusEl(dialog.querySelector('[data-noir-coven-focus]'));
    } else if (state === 'error') {
      focusEl(dialog.querySelector('input[type="email"]'));
    } else {
      focusEl(dialog.querySelector('[data-noir-popup-focus]'));
    }
  }

  /* After a post the URL ends in #<form id>; the browser's scroll to that fragment runs
     after load and moves focus to the page. Put it back in the dialog then. */
  function refocusAfterLoad() {
    function run() {
      window.requestAnimationFrame(function () {
        if (isOpen() && !dialog.contains(document.activeElement)) focusState();
      });
    }
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  function show(how) {
    dialog = dialog && document.contains(dialog) ? dialog : find();
    if (!dialog || isOpen()) return false;
    disarm();
    warmSeal();
    syncLabel();
    mode = how;
    if (Noir.dialog && typeof Noir.dialog.open === 'function') {
      Noir.dialog.open(dialog);
    } else {
      try {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      } catch (e) {
        dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('noir-dialog-open');
      focusEl(dialog.querySelector('.noir-popup__close'));
    }
    startSheet();
    focusState();
    return true;
  }

  function hide() {
    if (!dialog || !isOpen()) return;
    if (Noir.dialog && typeof Noir.dialog.close === 'function') {
      Noir.dialog.close(dialog);
    } else {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      closed();
    }
  }

  /* Runs once per close, whichever way it closed (noir-ui event, native close event). */
  function closed() {
    stopSheet();
    if (!mode) return;
    var how = mode;
    mode = null;
    if (!(Noir.dialog && typeof Noir.dialog.close === 'function')) {
      document.documentElement.classList.remove('noir-dialog-open');
    }
    if ((how === 'auto' || how === 'result') && !joined()) write(CLOSED, Date.now());
  }

  function autoOpen() {
    if (!eligible()) return;
    if (!write(SHOWN, Date.now())) return;
    erase(CLOSED);
    show('auto');
  }

  /* ---------- Triggers ---------- */
  function attempt() {
    retryTimer = 0;
    if (!eligible()) return;
    if (busy()) {
      wasBusy = true;
      retryTimer = window.setTimeout(attempt, RETRY);
      return;
    }
    if (wasBusy) {
      /* Let a closing drawer or modal finish before the pop-up takes the screen. */
      wasBusy = false;
      retryTimer = window.setTimeout(attempt, RETRY);
      return;
    }
    autoOpen();
  }

  function trigger() {
    if (fired) return;
    fired = true;
    disarm();
    attempt();
  }

  function depth() {
    var doc = document.documentElement;
    var body = document.body;
    var height = Math.max(doc.scrollHeight, body ? body.scrollHeight : 0);
    var view = window.innerHeight || doc.clientHeight;
    var room = height - view;
    if (room <= 0) return 0;
    var y = window.pageYOffset || doc.scrollTop || 0;
    return (y / room) * 100;
  }

  function onScroll() {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(function () {
      scrollFrame = 0;
      if (depth() >= config.scroll) trigger();
    });
  }

  function arm() {
    if (armed || fired) return;
    armed = true;
    delayTimer = window.setTimeout(trigger, config.delay * 1000);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    if (document.readyState === 'complete') warmSeal();
    else window.addEventListener('load', warmSeal, { once: true });
  }

  function disarm() {
    if (delayTimer) window.clearTimeout(delayTimer);
    delayTimer = 0;
    if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
    scrollFrame = 0;
    window.removeEventListener('scroll', onScroll);
    armed = false;
  }

  function stopAll() {
    disarm();
    if (retryTimer) window.clearTimeout(retryTimer);
    retryTimer = 0;
    fired = true;
  }

  /* ---------- Setup ---------- */
  function bind(d) {
    if (d.__noirPopupBound) return;
    d.__noirPopupBound = true;
    d.addEventListener('noir:dialog:close', function (e) {
      if (e.target === d) closed();
    });
    d.addEventListener('close', function () {
      if (d === dialog) closed();
    });
    var scroller = d.querySelector('.noir-popup__scroll');
    if (scroller) scroller.addEventListener('scroll', onSheetScroll, { passive: true });
    /* Phone: a tap on the backdrop while the email field is in use only puts the keyboard
       away (the pop-up stays, with what was typed); the next tap closes it. The press is
       checked, because Android moves focus off the field before the click. */
    d.addEventListener('pointerdown', function (e) {
      pressWhileTyping = e.target === d && phone() && typing();
    });
    d.addEventListener('click', function (e) {
      if (e.target !== d || !pressWhileTyping) return;
      pressWhileTyping = false;
      e.preventDefault();
      e.stopPropagation();
      var active = document.activeElement;
      if (active && d.contains(active) && typeof active.blur === 'function') active.blur();
    });
  }

  function setup() {
    dialog = find();
    if (!dialog) return false;
    config = {
      delay: number(dialog.getAttribute('data-delay'), 8, 0, 600),
      scroll: number(dialog.getAttribute('data-scroll'), 40, 1, 100),
      suppress: number(dialog.getAttribute('data-suppress'), 30, 0, 3650)
    };
    bind(dialog);
    return true;
  }

  function start() {
    if (!setup()) return;
    if (designMode()) return;
    if (sentHere()) {
      if (read(SHOWN) === null) write(SHOWN, Date.now());
      erase(CLOSED);
      if (show('result')) refocusAfterLoad();
      return;
    }
    if (!eligible()) return;
    /* A page that returns from another form's post (the contact letter, or a Coven form
       on the page) jumps to that form, which would count as scrolling: someone who has
       just written to us should not get the pop-up on that page. It can still open on
       the next page they visit. */
    if (/(^|[?&])(contact_posted|customer_posted)=/.test(window.location.search || '')) return;
    arm();
  }

  /* "Start shopping" closes the pop-up, then its link goes on (unless it points to this
     page, where closing is enough; after a post the URL carries ?customer_posted=true). */
  document.addEventListener('click', function (e) {
    var t = e.target;
    var link = t && t.closest ? t.closest('[data-noir-coven-start]') : null;
    if (!link || !dialog || !dialog.contains(link)) return;
    var same = false;
    try {
      var url = new URL(link.href, window.location.href);
      same = url.origin === window.location.origin && url.pathname.replace(/\/$/, '') === window.location.pathname.replace(/\/$/, '');
    } catch (err) {}
    if (same) e.preventDefault();
    hide();
  });

  /* Any [data-noir-popup-open] control (the product page's "Join the Coven for 20% off")
     opens the pop-up now, as Noir.popup.open() does: past every rule, even after it was
     dismissed, and writing nothing, so closing it again changes no timer. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    var opener = t && t.closest ? t.closest('[data-noir-popup-open]') : null;
    if (!opener) return;
    if (!dialog || !document.contains(dialog)) setup();
    if (!dialog) return;
    e.preventDefault();
    stopAll();
    /* Safari does not focus a clicked button; focus it so closing returns here. */
    if (document.activeElement !== opener && typeof opener.focus === 'function') {
      try {
        opener.focus({ preventScroll: true });
      } catch (err) {}
    }
    show('manual');
  });

  /* Without noir-ui.js: the X, the dismiss link and the backdrop still close it. */
  document.addEventListener('click', function (e) {
    if (Noir.dialog || !dialog || !isOpen()) return;
    var t = e.target;
    if (t === dialog || (t.closest && t.closest('[data-noir-dialog-close]') && dialog.contains(t))) {
      e.preventDefault();
      hide();
    }
  });

  /* A signup anywhere stops the pop-up from opening later on this page (and the joined
     flag keeps it shut on every later page). A signup inside the open pop-up switches it
     to its success state in place (noir-coven.js); the dialog takes that title as its name. */
  document.addEventListener('noir:coven:joined', function () {
    stopAll();
    if (!isOpen()) return;
    syncLabel();
    /* The sheet may have been scrolled down to the field: show the welcome from its top. */
    var scroller = dialog.querySelector('.noir-popup__scroll');
    if (scroller) scroller.scrollTop = 0;
  });

  /* Phone: once the field is in use, fit the sheet to what is visible (again on every
     change of the visual viewport while the keyboard comes and goes). */
  document.addEventListener('focusin', function (e) {
    if (sheetOn && dialog && dialog.contains(e.target)) queueFit();
  });
  if (window.matchMedia) {
    var phoneQuery = window.matchMedia(PHONE);
    var onPhoneChange = function () {
      if (sheetOn) queueFit();
    };
    if (typeof phoneQuery.addEventListener === 'function') phoneQuery.addEventListener('change', onPhoneChange);
    else if (typeof phoneQuery.addListener === 'function') phoneQuery.addListener(onPhoneChange);
  }

  /* Theme editor: open while the section is selected, close when it is not. A settings
     change renders the section again; the new dialog reopens if the section is selected. */
  var selected = false;
  function inSection(e) {
    return !!(e.target && e.target.querySelector && e.target.querySelector('dialog[data-noir-popup]'));
  }
  document.addEventListener('shopify:section:unload', function (e) {
    if (!inSection(e)) return;
    stopSheet();
    mode = null;
  });
  document.addEventListener('shopify:section:load', function (e) {
    if (!inSection(e)) return;
    mode = null;
    setup();
    if (selected) show('editor');
  });
  document.addEventListener('shopify:section:select', function (e) {
    if (!inSection(e)) return;
    selected = true;
    setup();
    show('editor');
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (!inSection(e)) return;
    selected = false;
    hide();
  });

  Noir.popup = {
    __ready: true,
    open: function () {
      if (!dialog) setup();
      return show('manual');
    },
    close: hide
  };

  /* Deferred scripts run once the DOM is parsed and noir-coven.js has routed by then; a
     late insert waits for either. */
  function boot() {
    if (Noir.coven && typeof Noir.coven.ready === 'function' && !Noir.coven.ready()) {
      document.addEventListener('noir:coven:ready', start, { once: true });
      return;
    }
    start();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
